import {Component, HostListener, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {DefaultAssetRepresentation} from "../../../../model/default-asset-representation";
import {DefaultAssetRepresentationsService} from "../../../../services/default-asset-representations.service";
import {
  DefaultAssetRepresentationPersistRequest
} from "../../../../model/requests/default-asset-representation-persist-request";

@Component({
  selector: 'app-default-asset-representations-form',
  templateUrl: './default-asset-representations-form.component.html',
  styleUrls: ['./default-asset-representations-form.component.scss']
})
export class DefaultAssetRepresentationsFormComponent implements OnInit {

  public representation: DefaultAssetRepresentation;

  public representationForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { oldName: string };

  icon: any;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private defaultAssetRepresentationsService: DefaultAssetRepresentationsService,
              private commonMessagesService: CommonMessagesService) {
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[1].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.representation = new DefaultAssetRepresentation();
    this.createForm(this.representation);
  }

  loadEditionForm(name): void {
    this.defaultAssetRepresentationsService.getDefaultAssetRepresentation(name).toPromise().then(response => {
      this.representation = response.defaultAssetRepresentation;
      this.editInfo = {oldName: this.representation.name};
      if (this.representation.icon) {
        this.icon = 'data:image/png;base64,' + this.representation.icon;
      }
      this.createForm(this.representation);
    });
  }

  private createForm(representation: DefaultAssetRepresentation): void {
    this.representationForm = this.formBuilder.group({
      name: [representation.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      description: [representation.description, [Validators.maxLength(255)]]
    });
    this.representationForm.markAsPending();
  }

  onUploadIcon(input: HTMLInputElement): void {
    const file: File = input.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => this.icon = reader.result;
  }

  persistRepresentationData(): void {
    if (!this.representationForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.defaultAssetRepresentationsService.createDefaultAssetRepresentation(persistData).toPromise();
    } else {
      promise = this.defaultAssetRepresentationsService.editDefaultAssetRepresentation(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/hw-catalog`], {state: {showView: 'asset-representations'}});
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  public cancel(): void {
    this.router.navigate([`/system/hw-catalog`], {state: {showView: 'asset-representations'}});
  }

  private buildPersistData(): DefaultAssetRepresentationPersistRequest {
    const utf8Encode = new TextEncoder();
    const formValue = this.representationForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      icon: this.icon ? (this.icon as string).split(',')[1] : null,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.representationForm.untouched;
  }

}
